import {
  DESKTOP_BREAKPOINT,
  isTablet,
  getPageType,
  isRefferedPage,
  isGoogleDiscover,
  isDesktop,
  getDevice,
  isArticle,
  isApp,
  isFacebookInAppBrowser,
  isPinterestInAppBrowser,
  isTikTokInAppBrowser,
  isInstagramInAppBrowser,
} from './config/device';
import { scaleAds } from './view/scaleAds';
import { pbjs } from './prebid/index';
import { resolveAdUnitPathGamSiteId, setDfpSection, isBlog, isTestSite, getVideoAdUnitPath } from './config/domain';
import {
  lazyloadSlot,
  refreshPrebidAd,
  refreshPrebidAds,
  getLoginId,
  isValidTargeting,
  isDebugTopAd,
} from './helpers/utils';
import amaLog, { amaWarn } from './helpers/log';
import { timeFor } from './helpers/time';
import { adsAbTest, getConnectionType } from './abtest/adsAbTest';
import { getGamVerticals } from './config/verticals';
/////////////////////////////////////////////////////////////////////////
import { resetVerticals } from './config/verticals';
/////////
import { getCookie } from './storage/cookies';
/////////////////////////////////////////////////////////////////////////
import { seenthis } from './config/seenthis';
/////////
/////////////////
///////////////////////////////////////////////
/////////

import { getModules } from './modules/index';
import adSettings from './config/adsettings';
import { resolveCountryCode } from './config/domain';
import { withinTopScrollerLimit, findAdConfigForPlacement, getSlotVerticalIndex } from './config/adslots';
import { deferredFirstWaveBatchManager, deferredGptLoadingManager } from './placement/deferredLoading';
import TargetingManager from './helpers/TargetingManager';
import { enableDMVerticals, updateMultivideoAdTagUrl } from './config/adschedule';
/////////////////
////////
/////////////////
/////////////////////////
////////////////
/////////////////////
///////////////
////////////////////////
////////////////////////////
///////////////////////
////////////////////
//////////////////////
////////////////////////////
/////////

import { useTopAds } from './config/platform';

export type InitManagerParams = {
  adsConfig?: IAdsConfig;
  _ppId?: string | undefined;
  uniqueId?: string;
  divId?: string;
  dfpSection?: string | undefined;
  pageNo?: number;
  hasConsent?: boolean;
  videoOnly?: boolean;
  isSecondArticle?: boolean;
  usePrebid?: boolean;
  pageReset?: boolean;
  firstLoad?: boolean;
  div?: HTMLElement;
  verticals?: string | string[];
};

/**
 * AdsManager will make life a tad easier for developers to deal
 * with ads as it can feel like a black magic most of the times.
 *
 * We construct and set up ad slots in this class so that the ads are
 * ready to send and receive information to and from Google DFP Ads.
 */
class AdsManager implements IAdsManager {
  public adUnitPath: string;
  public videoAdUnitPath: string;
  public Ads: IAdRaw[];
  public Ad: IAdRaw;
  public adsConfig: IAdsConfig;
  public AdUnits: object[];
  public PrebidAdUnits: object[];
  public listeners: IListener[];
  public uniqueId: string;
  public ppId: string | undefined;
  public pageSlots: { [key: string]: IAdRaw[] } = {};
  public adSlots: { [key: string]: IAdRaw } = {};
  public device: string;
  public usePrebid: boolean;
  public renderMargin: string;
  public firstLoad = true;
  private gLazyLoadEnabled: boolean = false;
  public hasConsent: boolean;
  public videoOnly: boolean;
  public isSecondArticle: boolean;
  public explicitContent: boolean;
  public firstVideoAutoStart: boolean;
  public countryCode: string;
  public loginId: string | boolean | undefined;
  public hasVideoBidsForPage: number[] = [];
  public pageNo: number = 0;
  public processedDivIds: Set<string>;
  private belowFoldThreshold: { mobile: number; desktop: number } = { mobile: 1.5, desktop: 1 }; // Deferred if more than viewport heights below the fold
  private firstWaveNonLazyAds: Map<string, IAdRaw> = new Map();
  private firstWaveNonLazyScheduled: boolean = false;
  private firstNonLazyBatchCompleted: boolean = false;
  private pendingLazyloadAds: Map<string, IAdRaw> = new Map();
  private pendingLazyloadTimer: number | null = null;
  private lazyloadBatchDelayMs: number = 60;
  private loadedPage: boolean = false;
  public usePoliticalAds: boolean = false;
///////////////////////////////////////////////////////////////////////////
  // Keyed by pageNo so each article's own verticals are only ever applied to
  // its own placements - never leaked onto placements from another pageNo.
  private verticalsByPageNo: Map<number, string | string[] | undefined> = new Map();
///////////

  public initConfig(params: InitManagerParams) {
    const { adsConfig, _ppId, dfpSection, hasConsent, videoOnly, usePrebid, pageReset } = params;

    amaLog('initConfig AdManager ', params);

    this.hasConsent = typeof hasConsent !== 'undefined' ? hasConsent : this.hasConsent;
    this.videoOnly = videoOnly;
    this.usePrebid = typeof usePrebid !== 'undefined' ? usePrebid : this.usePrebid;
    this.ppId = typeof _ppId !== 'undefined' ? _ppId : this.ppId;
    this.countryCode = resolveCountryCode();

    this.adsConfig = typeof adsConfig !== 'undefined' ? adsConfig : this.adsConfig;
    this.usePoliticalAds = this.adsConfig?.enablePoliticalAd === 'true';
    this.Ads = this.adsConfig?.ads || [];
    this.configureDeferredGptThresholds();
/////////////////////
////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////
////////////////////////////////////////
/////////////////////////////
//////////////////////////
///////
/////////////
    if (this.usePrebid) {
      // Set verticals for prebid if not set before
      enableDMVerticals(this.adsConfig?.verticals);
    }
    if (this.usePoliticalAds) {
      this.dmQueue(() => {
        // Word "directRendering" must be included so DM build system enables the module
        if (typeof pbjs?.rp?.mergeConfig === 'function') {
          pbjs.rp.mergeConfig({
            directRendering: {
              gptRefreshOverride: true, // Automatically intercepts GPT refresh and drops DR-rendered slots!
              schema: {
                fields: ['bidder', 'dealId'], // Match political bids from Adnuntius/Magnite
                rules: [['adnuntius', 'adn-political']],
              },
            },
          });
          amaLog('DM Direct Rendering Module configured for political ads');
        }
      });
    }
    pageReset && this.resetConfigs(dfpSection);

    if (this.videoOnly) {
      this.initializeVideoPageAds();
    }
  }

  private configureDeferredGptThresholds(): void {
    if (!this.usePoliticalAds) {
      deferredGptLoadingManager.resetBatchViewportDistance();
      return;
    }

    const politicalBatchViewportDistance = {
      desktop: ((this.adsConfig?.fetchMarginPercentDesk ?? 40) + 25) / 100,
      mobile: ((this.adsConfig?.fetchMarginPercentMob ?? 40) + 25) / 100,
    };

    deferredGptLoadingManager.setBatchViewportDistance(politicalBatchViewportDistance);
    amaLog('DEFER[GPT] - Applied political thresholds from accConfig', politicalBatchViewportDistance);
  }

  // Whether to defer GPT loading (true) or let it load instantly (false) for this placement.
  public skipGptInstantLoading(
    div?: HTMLElement,
    threshold: { mobile: number; desktop: number } = this.belowFoldThreshold,
  ): boolean {
    if (!div) return false;
    // Check if div is within viewport or within threshold
    const rect = div.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const vhThreshold = windowHeight * (isDesktop() ? threshold.desktop : threshold.mobile);
    return rect.top > windowHeight + vhThreshold || rect.bottom < -vhThreshold;
  }

  public initAd(params: InitManagerParams) {
    const { hasConsent, pageNo, isSecondArticle, divId, pageReset, div, verticals } = params;

    amaLog('initAd AdManager ', params);

    this.hasConsent = typeof hasConsent !== 'undefined' ? hasConsent : this.hasConsent;

    pageReset && this.resetConfigs();

    const bypassGptDefer = divId.startsWith('top_ad');
    // Don't defer GPT loading for top ads or if ad is within threshold/view
    const deferGptLoading = !bypassGptDefer && this.skipGptInstantLoading(div);
    if (deferGptLoading) {
      const runDeferredAd = () => this.gptQueue(() => this.initializeAd(divId, pageNo, isSecondArticle));
      deferredGptLoadingManager.schedule(runDeferredAd, div);
    } else {
      this.gptQueue(() => this.initializeAd(divId, pageNo, isSecondArticle, verticals));
    }
  }

  private resetConfigs(dfpSection?: string) {
    this.processedDivIds = new Set<string>();
    this.adSlots = {};
    this.loadedPage = false;
/////////////////////////////////////////////////////////////////////////////
    this.verticalsByPageNo.clear();
/////////////
    this.firstWaveNonLazyAds.clear();
    this.firstWaveNonLazyScheduled = false;
    this.firstNonLazyBatchCompleted = false;
    this.resetLazyloadBatch();
    deferredFirstWaveBatchManager.reset();
    deferredGptLoadingManager.reset();
    this.adsConfig.dfpSection = setDfpSection(dfpSection);
    this.adsConfig.gamSiteId = resolveAdUnitPathGamSiteId(this.adsConfig.gamSiteId);
    this.adUnitPath = `/${this.adsConfig.networkCode}/${this.adsConfig.gamSiteId}/${this.adsConfig.dfpSection}`;
    getVideoAdUnitPath(this.adsConfig, this.videoOnly, adSettings);
  }

  private initializeVideoPageAds(pageNo?: number): void {
    this.setupPage(pageNo);

    this.requestPrebidVideoBids(pageNo);
    this.initDone();
  }

  private initializeAd(divId: string, pageNo: number, isSecondArticle: boolean, verticals?: string | string[]): void {
    // Using Observer Without UniqueId

    if (this.adSlots[divId]) {
      amaLog(`initializeAd ${divId} already initialized. Skipping...`);
      return;
    }

    amaLog('initializeAd for divId - ', divId, pageNo, this.adsConfig.dfpSection, this.adSlots[divId]);

    // Find matching ad config
    const adTarget = findAdConfigForPlacement(this.adsConfig, divId);

    if (adTarget) {
      const adConfig = { ...adTarget, divId: divId };
///////////////////////
/////////////////////////////////////////////////
///////////////
      this.adSlots[divId] = adConfig;

      this.setupPage(pageNo);
///////////////////////////////////////////////////////////////////////////////
      this.setVerticals(pageNo, verticals);
///////////////
      this.ensureGoogleLazyload();

      const ad = this.setupAdSlot(this.adSlots[divId], pageNo, isSecondArticle);
      if (ad) {
        this.googleInitializeAdSlot(this.adSlots[divId]);

        // First, all slots Without LazyLoad will be loaded, then Lazyload will be enabled for the rest of the slots

        if (!this.refreshAdWithOutLazyload(this.adSlots[divId], pageNo)) {
          this.refreshAdWithLazyload(this.adSlots[divId], pageNo);
        }
      }

      this.firstLoad && isTablet && scaleAds();

      if (!this.hasVideoBidsForPage[pageNo]) {
        this.hasVideoBidsForPage[pageNo] = 1;
        this.requestPrebidVideoBids(pageNo);
      }
    } else {
      amaWarn('init divId - placements divId not found in Config: ', divId);
    }
  }

  private initDone(): void {
    this.firstLoad = false;
  }

  public invokeCustomVideoPlayer(adTagUrl: any) {
    amaLog(`DM requestVideoBids videoBidResult ${timeFor('prebidVidDone')} `, adTagUrl);

    window.amaTagUrl = adTagUrl;
  }
  public invokeVideoPlayer(videoBidResult: any, bids: any) {
    amaLog(`DM requestVideoBids videoBidResult ${timeFor('prebidVidDone')} `, videoBidResult, bids);

    window.amaTagUrl = videoBidResult?.adTagUrl;
    window.ama.adsBids = bids;
  }

  public loadGoogleLazyload(): void {
    if (this.gLazyLoadEnabled) return;

    const { useGLazyLoad } = getModules();
    if (!useGLazyLoad) {
      this.gLazyLoadEnabled = true;
      return;
    }
    amaLog('useGLazyLoad ');

    const fetchMarginPercent = isDesktop()
      ? this.adsConfig.fetchMarginPercentDesk
      : this.adsConfig.fetchMarginPercentMob ?? 40;
    let renderMarginPercent = isDesktop()
      ? this.adsConfig.renderMarginPercentDesk
      : this.adsConfig.renderMarginPercentMob ?? 20;

    if (adsAbTest() === 'c') {
      const margins = [5, 10, 15, 20, 25, 30];
      if (!this.renderMargin) {
        this.renderMargin = margins[Math.floor(Math.random() * margins.length)].toString();
      }
      renderMarginPercent = parseInt(this.renderMargin);

      TargetingManager.setPageTargeting({
        lazyload: `c${this.renderMargin}`,
        RenderMargin: this.renderMargin,
        connectionRtt: getConnectionType(),
      });
    } else {
      TargetingManager.setPageTargeting({ lazyload: 'a' });
    }

    window.googletag.setConfig({
      lazyLoad: {
        fetchMarginPercent,
        renderMarginPercent,
        mobileScaling: 1.0,
      },
    });
    this.gLazyLoadEnabled = true;
  }

  public requestPrebidVideoBids(pageNo: number): void {
/////////////////////

    const { usePrebidVideo, adTags, useRequestMultiVideoBids } = getModules();
    if (adTags) {
      amaLog('AdTags are present, skipping requestPrebidVideoBids');
    } else if (!usePrebidVideo) {
      amaLog(`Prebid video is disabled in bundle, skipping requestPrebidVideoBids`);
    } else if (!this.usePrebid) {
      amaLog(`Prebid video is disabled from ACC or having no consent, skipping requestPrebidVideoBids`);
    } else {
      const hasVideo =
        document.querySelectorAll('[id^="video-"]').length > 0 ||
        document.querySelectorAll('[id^="jw_player"]').length > 0 ||
        document.querySelectorAll('[class^="js-jwplayer__container"]').length > 0 ||
        document.querySelectorAll('[class^="jw-flag"]').length > 0;

      amaLog(`requestPrebidVideoBids hasVideo:${hasVideo} videoOnly:${this.videoOnly} and pageNo ${pageNo}`);

      this.dmQueue(() => {
        // Timeout (ms) for bidrequests is set in DemandManager-dashboard

        if (this.videoOnly || hasVideo) {
          amaLog(`requestVideoBids hasVideo:${hasVideo} videoOnly:${this.videoOnly} and pageNo ${pageNo}`);
          if (!useRequestMultiVideoBids) {
            if (!this.usePoliticalAds) {
              amaLog('Non Political ads regular###', !this.usePoliticalAds);
              pbjs.rp.requestVideoBids({
                adSlotName: getVideoAdUnitPath(this.adsConfig, this.videoOnly, adSettings),
                playerSize: [640, 360],
                callback: this.invokeVideoPlayer,
                data: {},
              });
            } else {
              const adSlotName = getVideoAdUnitPath(this.adsConfig, this.videoOnly, adSettings);
              pbjs.rp.requestVideoBids({
                adSlotName: adSlotName,
                playerSize: [640, 360],
                adServer: 'custom',
                callback: (bids: any) => {
                  const winner = pbjs.getHighestCpmBids(adSlotName)[0];
                  const isPolitical = winner && winner.dealId === 'adn-political';
                  if (isPolitical && winner.vastUrl) {
                    const brokenUrl = winner.vastUrl;
                    const correctedUrl = brokenUrl.replace('int=dmpbjs?', 'int=dmpbjs&');
                    amaLog('Political ads#', correctedUrl);
                    this.invokeCustomVideoPlayer(correctedUrl);
                  } else {
                    // 13012.js registers the video ad server module as `gam`, not `dfp` - fall back to `dfp` for older builds
                    const videoAdServer = pbjs.adServers?.gam || pbjs.adServers?.dfp;
                    const adTagUrl = videoAdServer?.buildVideoUrl({
                      adUnit: adSlotName,
                      params: {
                        iu: adSlotName,
                        sz: '640x360',
                      },
                    });
                    amaLog('Non Political ads ', adTagUrl);
                    this.invokeCustomVideoPlayer(adTagUrl);
                  }
                },
                data: {},
              });
            }
          } else {
            pbjs.rp?.ext?.requestMultiVideoBids({
              adSlotName: getVideoAdUnitPath(this.adsConfig, this.videoOnly, adSettings),
              playerSize: [640, 360],
              prerollCount: 2,
              allowFallbacks: true,
              returnFullBids: false,
              callback: (multiVideoBidResult?: { preroll?: { adTagUrl: string }[] }) => {
                amaLog(`requestMultiVideoBids callback ${timeFor('prebidVidDone')} `, multiVideoBidResult);

                const prerollBids = multiVideoBidResult?.preroll;

                if (!prerollBids?.length) {
                  amaLog('No preroll video bids returned.');
                  return;
                }

                const updatedPreroll = prerollBids.map((item) => {
                  const updatedUrl = updateMultivideoAdTagUrl(item.adTagUrl, this.adsConfig?.verticals);
                  return { adTagUrl: updatedUrl };
                });
                const updatedMultiVideoBid = {
                  preroll: updatedPreroll,
                };

                interface VMAPOptions {
                  videoDuration: number;
                  midrollPercent: Array<number>;
                }

                const options: VMAPOptions = {
                  videoDuration: 115,
                  midrollPercent: [50],
                };
                amaLog('updatedMultiVideoBid:', updatedMultiVideoBid);

                let VMAPCacheUrl = null;
                pbjs.rp.ext
                  .getVMAPCacheUrl({
                    multiVideoBidResult: updatedMultiVideoBid,
                    options,
                  })
                  .then((cacheUrl: string) => {
                    VMAPCacheUrl = cacheUrl;
                    amaLog('VMAPCacheUrl:', VMAPCacheUrl);
                    window.amaVMAPCacheUrl = VMAPCacheUrl;
                  })
                  .catch((error: any) => {
                    amaLog('No VMAPCacheUrl', error);
                  });
              },
            });
          }
        }
      });
    }
/////////////
  }

  /**
   * Queue
   *
   * Helps us push our functions to the google call stack and
   * keep our context.
   *
   * @param fn fn
   * @return void
   */
  public gptQueue(fn: Function): void {
    window.googletag.cmd.push(fn.bind(this));
  }

  /**
   * PrebidJS Queue
   *
   * Helps us push functions to the prebid call stack and keep
   * our context.
   *
   * @param fn fn
   *
   * @return void
   */
  public dmQueue(fn: Function): void {
    pbjs.que.push(() => fn.apply(this));
  }

  /**
   * This method setup ad-settings on a page-level. For instance it
   * iterates window.aller_ga.verticals object and targets verticals
   * that is common for all ads
   * @return void
   */
  private setupPage(pageNo: number): void {
    if (this.loadedPage || pageNo > 1) return;

    this.loadedPage = true;

    this.gptQueue(() => {
      this.device = getDevice();

      const pageTargeting: Record<string, string | string[]> = {
        hour: new Date().getHours().toString(),
        page: adSettings.explicitContent?.toString() === 'true' ? 'explicit' : getPageType(),
        consent: this.hasConsent ? 'yes' : 'no',
      };

      if (isTestSite()) pageTargeting.stage = 'true';

      const pathComponents = window.location.pathname.split('/');
      pageTargeting.url = window.location.hostname;
      pathComponents[1] && (pageTargeting['url-category'] = pathComponents[1]);
      pathComponents[2] && (pageTargeting['url-content'] = pathComponents[2]);

      isRefferedPage() && (pageTargeting['user-agent'] = 'search');
      isGoogleDiscover() && (pageTargeting['user-agent'] = 'discover');
      isApp() && (pageTargeting['user-agent'] = 'app');
      (isFacebookInAppBrowser() || isPinterestInAppBrowser() || isTikTokInAppBrowser() || isInstagramInAppBrowser()) &&
        (pageTargeting['user-agent'] = 'social');
      const loginCookie = getCookie('login_token');
      loginCookie && (pageTargeting.page = 'login');
      isBlog() && (pageTargeting.page = 'blog');

      // window.ama?.pageTargets will override
      Object.entries(window.ama?.pageTargets || {}).forEach(([key, value]) => {
        if (isValidTargeting({ key, value }) && key !== 'page_type') {
          pageTargeting[key] = value;
        }
      });
      if (this.hasConsent && this.ppId) {
        window.googletag.pubads().setPublisherProvidedId(this.ppId);
      }

      // Norway spesific targeting
///////////////////////
///////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////

/////////////////////////
/////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////
///////

//////////////////////////////////////////
/////////////////////////////////////////////////////////
//////////////////////////////////////////////
////////////////////////////////////////////
////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
///////

////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////

//////////////////////////////////////////////////////////////////
/////////////////////////////

///////////////

      window.googletag.setConfig({
        targeting: pageTargeting,
      });
    });
  }

///////////////////////////////////////////////////////////////////////////
  /**
   * Sets/caches the 'Vertikal' value for a page, keyed by its own pageNo.
   * Called from initializeAd for every placement so all Vertikal handling
   * lives in one place, rather than split between here and setupPage.
   * This is intentionally NOT applied as page-level targeting - doing so
   * would overwrite the 'Vertikal' value for every slot on the page,
   * including ones from a different pageNo that haven't been requested yet.
   * setupAdSlot looks the value up per slot via its own pageNo instead.
   * Uses the verticals passed in via window.ama.handleContent when given;
   * otherwise derives them from window.allerPageData/window.aller_ga.
   * @return void
   */
  private setVerticals(pageNo: number, verticals?: string | string[]): void {
    if (this.verticalsByPageNo.has(pageNo)) return;

    this.gptQueue(() => {
      let newVerticals = verticals;
      if (newVerticals === undefined) {
        resetVerticals();
        newVerticals = getGamVerticals(this.adsConfig.verticals);
      }
      amaLog('setVerticals - Caching Vertikal for pageNo', pageNo, newVerticals);
      this.verticalsByPageNo.set(pageNo, newVerticals);
    });
  }
///////////

  private setupAdSlot = (ad: IAdRaw, pageNo: number, isSecondArticle: boolean): IAdRaw => {
    let domId = ad.divId;
    const showTopAd = (useTopAds() && withinTopScrollerLimit()) || isDebugTopAd();
    if (ad.target === 'top_ad' && !showTopAd) {
      amaLog(`Top ad ${domId} is not eligible to be shown.`);
      return ad;
    }
    this.loginId ??= getLoginId() || false;
    const { useNewAdUnitPath } = getModules();
    let adUnitPath = this.adUnitPath;
    if (useNewAdUnitPath) {
      let targetSegment = ad.target;
      if (ad.target !== 'top_ad') {
        amaLog(`newAdunitPath PlacementName Before ## ${ad.target}`);
        const slotOrder = getSlotVerticalIndex(domId, pageNo);
        targetSegment = slotOrder <= 8 ? `ad_content_${slotOrder}` : 'ad_content_x';
        amaLog(`newAdunitPath PlacementName after ## ${targetSegment}`);
        const element = document.getElementById(domId);

        if (element) {
          const parts = domId.split('-');
          const suffix = parts.length > 1 ? `-${parts.slice(1).join('-')}` : '';
          const newDomId = `${targetSegment}${suffix}`;

          element.id = newDomId;
          element.setAttribute('data-aller-ad-placement', targetSegment);

          domId = newDomId;
          ad.divId = newDomId;
        }

        ad.target = targetSegment;
      }

      adUnitPath = `${adUnitPath}/${targetSegment}`;
    }

/////////////////////////////////////////////////////////////////////////////
    this.adsConfig?.enableSeenThis === 'true' && seenthis(adUnitPath, domId);
/////////////

    ad.adSlot = window.googletag.defineSlot(adUnitPath, [...ad.sizes?.mobile, ...ad.sizes?.desktop], domId);

    const mapping = window.googletag
      .sizeMapping()
      .addSize([0, 0], ad.sizes.mobile)
      .addSize(DESKTOP_BREAKPOINT, ad.sizes.desktop)
      .build();
    ad.adSlot.defineSizeMapping(mapping).addService(window.googletag.pubads());

    if (this.countryCode === 'no') {
      TargetingManager.setSlotTargeting(ad.adSlot, { pos: ad.placement.slice(3) });
    } else {
      TargetingManager.setSlotTargeting(ad.adSlot, { pos: ad.placement });
    }

/////////////////////////////////////////////////////////////////////////////
    // Slot-level (not page-level) so each slot only ever gets the Vertikal
    // value cached for its own pageNo, never one leaked from another pageNo.
    TargetingManager.setSlotTargeting(ad.adSlot, { Vertikal: this.verticalsByPageNo.get(pageNo) ?? '' });
/////////////

    if (isArticle() && pageNo && ad.target !== 'top_ad')
      TargetingManager.setSlotTargeting(ad.adSlot, { articleno: `${pageNo}` });

    if (isSecondArticle && pageNo === 2 && ad.target !== 'top_ad')
      TargetingManager.setSlotTargeting(ad.adSlot, { secondarticle: 'true' });
    this.loginId && TargetingManager.setSlotTargeting(ad.adSlot, { loginid: this.loginId.toString() });
    this.usePrebid && TargetingManager.setSlotTargeting(ad.adSlot, { demandmanager: '1' });
/////////////////////
//////////////////////////////////////////////////////////////////////////////////
/////////////

//////////////////////////////////////////
//////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////
/////
/////////////

    return ad;
  };

  private googleInitializeAdSlot(ad: IAdRaw): void {
    this.gptQueue(() => {
      const adDiv = ad.divId ?? `${ad.target}${this.uniqueId}`;
      amaLog('googleInitializeAdSlot', adDiv);
      window.googletag.display(adDiv);
    });
  }

  public refreshAd(ad: IAdRaw): void {
    amaLog('refresh (load) Ad ', ad.adSlot?.getSlotElementId());

    if (this.usePrebid) {
      refreshPrebidAd(ad, undefined, this.usePoliticalAds, {
        observerOffset: this.getPoliticalPrebidObserverOffset(),
      });
    } else {
      window.googletag.pubads().refresh([ad.adSlot], { changeCorrelator: false });
    }
  }

  private getPoliticalPrebidObserverOffset(): number | undefined {
    if (!this.usePoliticalAds) return undefined;

    const offset = isDesktop() ? this.adsConfig?.prebidRenderMarginDesk : this.adsConfig?.prebidRenderMarginMob;
    return typeof offset === 'number' && offset > 0 ? offset : undefined;
  }

  public refreshAdWithOutLazyload(ad: IAdRaw, pageNo = 1): boolean {
    if (!lazyloadSlot(ad, pageNo)) {
      amaLog('refresh Ad WITHOUT Lazyload ', ad.adSlot?.getSlotElementId());

      const { useGLazyLoad, useFirstWaveBatching } = getModules();
      const isFirstWavePlacement = pageNo === 1 && Array.isArray(useGLazyLoad) && useGLazyLoad.includes(ad.placement);
      const shouldBatchFirstWave = !this.firstNonLazyBatchCompleted && isFirstWavePlacement;

      if (shouldBatchFirstWave && useFirstWaveBatching) {
        const slotId = ad.adSlot?.getSlotElementId();
        slotId && this.firstWaveNonLazyAds.set(slotId, ad);

        amaLog(`DEFER[firstWaveBatch] - Queued non-lazy ad for first wave batch: ${slotId}`);

        if (!this.firstWaveNonLazyScheduled) {
          this.firstWaveNonLazyScheduled = true;

          // Reuse deferred manager queue/timer for first non-lazy wave batch flush.
          deferredFirstWaveBatchManager.schedule(() => {
            const nonLazyAds = Array.from(this.firstWaveNonLazyAds.values());
            this.firstWaveNonLazyAds.clear();
            this.firstWaveNonLazyScheduled = false;
            this.firstNonLazyBatchCompleted = true;

            if (!nonLazyAds.length) return;

            amaLog(
              'DEFER[firstWaveBatch] - Refreshing Ad WITHOUT Lazyload (first batch)',
              nonLazyAds.map((item) => item.adSlot?.getSlotElementId()),
            );

            const flushDeferredPlacements = () => deferredGptLoadingManager.flushDeferredAds();

            if (this.usePrebid) {
              refreshPrebidAds(nonLazyAds, flushDeferredPlacements, this.usePoliticalAds);
              return;
            }

            const slots = nonLazyAds.map((item) => item.adSlot).filter(Boolean);
            slots.length && window.googletag.pubads().refresh(slots, { changeCorrelator: false });
            flushDeferredPlacements();
          });
        }

        if (this.firstWaveNonLazyAds.size >= useGLazyLoad.length) {
          deferredFirstWaveBatchManager.flushDeferredAds();
        }

        return true;
      }

      if (this.usePrebid) {
        refreshPrebidAd(ad, undefined, this.usePoliticalAds);
      } else {
        this.refreshAd(ad);
      }
      return true;
    }
    return false;
  }
  public refreshAdWithLazyload(ad: IAdRaw, pageNo = 1): void {
    if (lazyloadSlot(ad, pageNo)) {
      this.queueLazyloadRefresh(ad);
    }
  }

  private queueLazyloadRefresh(ad: IAdRaw): void {
    const slotId = ad.adSlot?.getSlotElementId();
    if (!slotId) return;

    this.pendingLazyloadAds.set(slotId, ad);

    if (this.pendingLazyloadTimer !== null) {
      return;
    }

    this.pendingLazyloadTimer = window.setTimeout(() => {
      this.flushLazyloadBatch();
    }, this.lazyloadBatchDelayMs);
  }

  private resetLazyloadBatch(): void {
    if (this.pendingLazyloadTimer !== null) {
      clearTimeout(this.pendingLazyloadTimer);
      this.pendingLazyloadTimer = null;
    }
    this.pendingLazyloadAds.clear();
  }

  private flushLazyloadBatch(): void {
    if (this.pendingLazyloadTimer !== null) {
      clearTimeout(this.pendingLazyloadTimer);
      this.pendingLazyloadTimer = null;
    }

    const lazyAds = Array.from(this.pendingLazyloadAds.values());
    if (!lazyAds.length) {
      return;
    }

    this.pendingLazyloadAds.clear();

    amaLog(
      'refresh Ad WITH Lazyload (batched)',
      lazyAds.map((ad) => ad.adSlot?.getSlotElementId()),
    );

    if (this.usePrebid) {
      refreshPrebidAds(lazyAds, undefined, this.usePoliticalAds, {
        observerOffset: this.getPoliticalPrebidObserverOffset(),
      });
      return;
    }

    const slots = lazyAds.map((ad) => ad.adSlot).filter(Boolean);
    slots.length && window.googletag.pubads().refresh(slots, { changeCorrelator: false });
  }

  private ensureGoogleLazyload(): void {
    if (!this.gLazyLoadEnabled) {
      this.loadGoogleLazyload();
    }
  }
}

export default new AdsManager();
